import jsPDF from "jspdf";
import React from "react";
import { wordpressPostIds } from "../constants/wordpress";
import { formatDate } from "../../shared/utils/formatters";

const postFields = "id,slug,excerpt,title,jetpack_featured_media_url,content,date_gmt,categories";
const postSummaryFields = "id,slug,excerpt,title,categories,jetpack_featured_media_url,date_gmt";

export const getWordpressPost = async (post: string, setter: any, subscribed: React.MutableRefObject<boolean>) => {
  fetch(`https://staging.trashwarrior.com/wp-json/wp/v2/posts/${post}?_fields=${postFields}`)
    .then((result) => result.json())
    .then((data) => subscribed.current && setter(data))
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    });
};

export const getWordpressPostBySlug = async (post: string, setter: any, subscribed: React.MutableRefObject<boolean>) => {
  fetch(`https://staging.trashwarrior.com/wp-json/wp/v2/posts?slug=${post}&&_fields=${postFields}`)
    .then((result) => result.json())
    .then((data) => subscribed.current && setter(data[0]))
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    });
};

export const getWordpressPostList = async (category: string, setter: any, subscribed: React.MutableRefObject<boolean>): Promise<void> => {
  fetch(`https://staging.trashwarrior.com/wp-json/wp/v2/posts?_fields=${postSummaryFields}&&per_page=100&categories=${category}`)
    .then((result) => result.json())
    .then((data: Array<any>) => {
      if (!subscribed.current) return;
      setter(data);
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
      return [];
    });
};

const decodeHtmlEntities = (text: string) => {
  const textArea = document.createElement("textarea");
  textArea.innerHTML = text;
  return textArea.value;
};

const cleanText = (text: string | null) => {
  return text?.replace(/\s+/g, " ").trim();
};

const extractTextWithStyle = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;

  const style = {
    isBold: false,
    isHeader: false,
  };

  if (div.querySelector("strong") || html.includes("<strong>")) {
    style.isBold = true;
  }

  if (div.querySelector("h1") || html.includes("<h1>")) {
    style.isHeader = true;
  }

  const text = cleanText(div.textContent);

  return { text, style };
};

const handleListItems = (
  doc: jsPDF,
  items: string[],
  cursorY: number,
  margin: number,
  lineHeight: number,
  pageWidth: number,
  maxHeight: number,
  indent = 0
) => {
  const bullet = "\u2022";

  for (const item of items) {
    const { text: cleanText, style } = extractTextWithStyle(item);

    if (cleanText) {
      if (style.isBold) {
        doc.setFont("Arial", "bold");
      }

      const lines = doc.splitTextToSize(cleanText, pageWidth - indent - 0.25);

      for (const line of lines) {
        if (cursorY + lineHeight > maxHeight) {
          doc.addPage();
          cursorY = margin;
        }

        doc.text(`${" ".repeat(indent)}${bullet} ${line}`, margin, cursorY);
        cursorY += lineHeight;
      }

      if (style.isBold) {
        doc.setFont("Arial", "normal");
      }

      cursorY += lineHeight * 0.5;
    }
  }
  return cursorY;
};

export const downloadAgreement = async (path: string, profile: any) => {
  const postId = wordpressPostIds[path];

  try {
    const response = await fetch(`https://staging.trashwarrior.com/wp-json/wp/v2/posts/${postId}?_fields=${postFields}`);
    const data = await response.json();
    const postTitle = decodeHtmlEntities(data?.title?.rendered ?? "");
    const postContent = decodeHtmlEntities(data?.content?.rendered?.split("#038;")?.join("") ?? "");

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
    });

    const lineHeight = 0.25;
    const margin = 1;
    let cursorY = margin;
    const pageWidth = 8.5 - 2 * margin;
    const maxHeight = 11 - 2 * margin;

    doc.setFontSize(16);
    doc.setFont("Arial", "bold");
    doc.text(postTitle, margin, cursorY, { maxWidth: pageWidth });
    doc.setFont("Arial", "normal");
    cursorY += lineHeight * 2;

    doc.setFontSize(12);

    const content = postContent.split(/<\/?(?:p|ul|ol|li|h1)(?:\s[^>]*)?>/).filter(Boolean);
    let isList = false;
    let listItems = [];

    for (const part of content) {
      if (part.includes("<ul") || part.includes("<ol")) {
        isList = true;
        listItems = [];
      } else if (part.includes("</ul>") || part.includes("</ol>")) {
        if (listItems.length > 0) {
          cursorY = handleListItems(doc, listItems, cursorY, margin, lineHeight, pageWidth, maxHeight, 0.25);
        }
        isList = false;
        listItems = [];
      } else if (isList) {
        listItems.push(part);
      } else {
        const { text, style } = extractTextWithStyle(part);

        if (text) {
          if (style.isHeader) {
            doc.setFontSize(14);
            doc.setFont("Arial", style.isBold ? "bold" : "normal");
          } else {
            doc.setFontSize(12);
            doc.setFont("Arial", style.isBold ? "bold" : "normal");
          }

          const lines = doc.splitTextToSize(text, pageWidth);
          for (const line of lines) {
            if (cursorY + lineHeight > maxHeight) {
              doc.addPage();
              cursorY = margin;
            }
            doc.text(line, margin, cursorY);
            cursorY += lineHeight;
          }

          doc.setFontSize(12);
          doc.setFont("Arial", "normal");
          cursorY += lineHeight;
        }
      }
    }

    cursorY += 0.5;
    const signatureText = path === "provider-agreement" ? profile?.providerAgreement?.electronicSignature : profile?.fullName;
    doc.setFont("Arial", "italic");
    doc.text(`${signatureText}`, margin, cursorY);
    cursorY += 0.2;

    const dateText =
      path === "provider-agreement" ? formatDate(profile?.providerAgreement?.date, 2) : formatDate(profile?.serviceProviderPolicy?.date, 2);
    doc.setFont("Arial", "italic");
    doc.text(`${dateText}`, margin, cursorY);


    doc.save(`${path}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error); // eslint-disable-line no-console
  }
};
